import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ResolvePublicCodeResponse } from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { ErrorAlert } from '../components/ui'

/**
 * Deep link QR `/r/<publicCode>` : après connexion, ouvre le rangement
 * (et bascule le foyer courant si besoin).
 */
export function ResolveCodePage() {
  const { t } = useTranslation()
  const { publicCode = '' } = useParams()
  const navigate = useNavigate()
  const { current, households, select } = useHousehold()

  const resolve = useQuery({
    queryKey: ['resolve-code', publicCode],
    enabled: Boolean(publicCode),
    retry: false,
    queryFn: () => api.get<ResolvePublicCodeResponse>(`/r/${encodeURIComponent(publicCode)}`),
  })

  useEffect(() => {
    if (!resolve.data) return
    const { householdId, location } = resolve.data
    if (current?.id !== householdId) {
      const known = households.some((h) => h.id === householdId)
      if (known) select(householdId)
    }
    navigate(`/storage?locationId=${encodeURIComponent(location.id)}`, { replace: true })
  }, [resolve.data, current?.id, households, select, navigate])

  if (resolve.isError) {
    return (
      <>
        <h1>{t('labels.resolveTitle')}</h1>
        <ErrorAlert error={resolve.error} />
        <p>
          <Link className="btn" to="/storage">
            {t('inventory.seeStorage')}
          </Link>
        </p>
      </>
    )
  }

  return <p role="status">{t('common.loading')}</p>
}
